import type { Finding } from './finding-dto';

/**
 * The shared grammar for entity references embedded in finding prose — `[item:401464]`,
 * `[objective:46]` — instead of a raw id or a hand-typed external key. Used on both sides:
 * `apps/sir` validates the agent's output against it, the frontend tokenizes finding text with it
 * to render resolved, potentially clickable labels. Keeping the grammar here means neither side can
 * drift from the other.
 */
export const referenceKinds = ['item', 'objective'] as const;
export type ReferenceKind = (typeof referenceKinds)[number];

/**
 * The regex *source*, not a compiled `/g` pattern — a shared global regex carries `lastIndex`
 * across callers that share the module, which silently drops matches on the second caller. Each
 * consumer compiles its own instance from this source.
 */
export const REFERENCE_SOURCE = String.raw`\[(item|objective):(\d+)\]`;

export interface ParsedReference {
  kind: ReferenceKind;
  id: number;
  /** The exact matched substring, e.g. `[item:401464]` — used as a stable React key by callers. */
  raw: string;
  /** Offset of the match within the source text. */
  index: number;
}

/**
 * Finds every reference token in `text`, in order of appearance. Returns an empty array for text
 * with no tokens, which is the common case and must be a cheap no-op.
 */
export const parseReferences = (text: string): ParsedReference[] => {
  const pattern = new RegExp(REFERENCE_SOURCE, 'g');
  const matches: ParsedReference[] = [];
  for (const match of text.matchAll(pattern)) {
    const [raw, kind, id] = match;
    if (!kind || !id || match.index === undefined) continue;
    matches.push({ kind: kind as ReferenceKind, id: Number(id), raw, index: match.index });
  }
  return matches;
};

export interface FindingReferences {
  items: number[];
  objectives: number[];
}

/**
 * The claim-bearing fields. A reference here cannot be quietly rewritten — what remained would
 * assert the same thing about nothing in particular — whereas one in supporting prose can. Only
 * backend validation distinguishes the two tiers.
 */
const CORE_TEXT_FIELDS = (finding: Finding): string[] => [
  finding.headline,
  finding.businessStatement,
  ...finding.recommendations.map((recommendation) => recommendation.target),
];

const TEXT_FIELDS = (finding: Finding): string[] => {
  const fields: string[] = [
    ...CORE_TEXT_FIELDS(finding),
    finding.caveat ?? '',
    finding.evidence.observed,
    finding.evidence.projected ?? '',
    finding.evidence.ruledOut ?? '',
    finding.evidence.trackRecord ?? '',
    ...(finding.evidence.sources ?? []).flatMap((source) => [source.label, source.statement]),
    ...(finding.evidence.alternatives ?? []).flatMap((alternative) => [
      alternative.statement,
      alternative.note,
    ]),
    ...finding.description.flatMap((block) => [block.lead, block.text, block.note ?? '']),
    ...finding.recommendations.flatMap((recommendation) => [
      recommendation.title,
      recommendation.businessTitle,
      recommendation.detail,
      recommendation.impact,
      ...(recommendation.steps ?? []),
      ...(recommendation.successCriterion ? [recommendation.successCriterion] : []),
    ]),
  ];
  for (const override of Object.values(finding.personaOverrides ?? {})) {
    fields.push(
      override.businessStatement,
      ...override.recommendations.flatMap((recommendation) => [
        recommendation.title,
        recommendation.businessTitle,
        recommendation.detail,
        recommendation.target,
        recommendation.impact,
        ...(recommendation.steps ?? []),
        ...(recommendation.deadline ? [recommendation.deadline] : []),
        ...(recommendation.successCriterion ? [recommendation.successCriterion] : []),
      ]),
    );
  }
  return fields;
};

/**
 * Every reference token across every text field of a finding, including per-persona overrides —
 * the set the frontend needs to resolve in one batch, and the set the backend validates against
 * the evidence ledger. Deduplicated per kind; `withheldFrom` reasons are plain prose about why a
 * finding is hidden and are not expected to carry references, so they are not scanned.
 */
export const collectFindingReferences = (finding: Finding): FindingReferences => {
  const items = new Set<number>();
  const objectives = new Set<number>();
  for (const field of TEXT_FIELDS(finding)) {
    for (const ref of parseReferences(field)) {
      (ref.kind === 'item' ? items : objectives).add(ref.id);
    }
  }
  return { items: [...items], objectives: [...objectives] };
};

/** The subset of `collectFindingReferences` appearing in `CORE_TEXT_FIELDS`. */
export const collectCoreFindingReferences = (finding: Finding): FindingReferences => {
  const items = new Set<number>();
  const objectives = new Set<number>();
  for (const field of CORE_TEXT_FIELDS(finding)) {
    for (const ref of parseReferences(field)) {
      (ref.kind === 'item' ? items : objectives).add(ref.id);
    }
  }
  return { items: [...items], objectives: [...objectives] };
};

/** A noun rather than a deletion, so the surrounding sentence stays grammatical. */
const STRIPPED_LABEL: Record<ReferenceKind, string> = {
  item: 'an unverified item',
  objective: 'an unverified objective',
};

/**
 * Rewrites the given references out of `text`, leaving every other token untouched, so the frontend
 * cannot render a chip for an entity the run never observed. Matched on kind AND id: `[item:46]`
 * and `[objective:46]` are different references that happen to share a number.
 */
export const stripReferences = (
  text: string,
  unresolved: readonly { kind: ReferenceKind; id: number }[],
): string => {
  if (unresolved.length === 0) return text;
  const strip = new Set(unresolved.map(({ kind, id }) => `${kind}:${id}`));
  const refs = parseReferences(text).filter((ref) => strip.has(`${ref.kind}:${ref.id}`));
  if (refs.length === 0) return text;

  // Rebuilt left to right so earlier edits never shift the offsets of later ones.
  let rebuilt = '';
  let cursor = 0;
  for (const ref of refs) {
    rebuilt += text.slice(cursor, ref.index) + STRIPPED_LABEL[ref.kind];
    cursor = ref.index + ref.raw.length;
  }
  return rebuilt + text.slice(cursor);
};
